import { Cell, Column, flexRender, Row, Table } from '@tanstack/react-table';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { CssFromFunction } from '@upright/ui/common/generic/types/types';
import { ComponentType, CSSProperties, Fragment } from 'react';
import styled, { css } from 'styled-components';
import { TableStyleProps, TableV2ChildComponentProps } from '../../types';
import { getTableCellCommonStyle, TableCellCommonStyleProps } from './table-cell-common';
import { tableV2CellTestId, tableV2RowTestId } from '@upright/test-ids/table-v2';
import { getFirstAndLastColumnIds } from '../../utils/utils';

export function TBody<T extends object>(props: {
  table: Table<T>;
  ChildComponent?: ComponentType<TableV2ChildComponentProps<T>>;
  getRowProps?: (row: Row<T>) => Partial<TableStyleProps>;
  getColumnProps?: (column: Column<T>) => Partial<TableStyleProps>;
  getCellProps?: (cell: Cell<T, unknown>) => Partial<TableStyleProps>;
  'data-testid'?: string;
  showVerticalBorders: boolean;
  className?: string;
  onRowClick?: (row: Row<T>) => void;
}) {
  const { table } = props;
  const rows = table.getRowModel().rows;
  const rowCount = rows.length;

  const { firstColumnId, lastColumnId } = getFirstAndLastColumnIds(table);

  return (
    <TbodyRoot className={props.className}>
      {rows.map((row, rowIndex) => {
        const rowProps = props.getRowProps?.(row) || {};
        const isLastRow = rowIndex === rowCount - 1;
        return (
          <Fragment key={row.id}>
            <Tr
              data-testid={rowProps['data-testid'] ?? tableV2RowTestId(props['data-testid'], row.id)}
              onClick={() => props.onRowClick?.(row)}
            >
              {row.getVisibleCells().map(cell => {
                const columnDef = cell.column.columnDef;
                const meta = columnDef.meta;

                const columnProps = props.getColumnProps?.(cell.column);
                const cellProps = props.getCellProps?.(cell);

                // If there are overlapping style props, the hierarchy is as follows:
                // cellProps > columnProps > rowProps > columnDef.meta.cellCss > columnDef.meta.css.
                const mergedStyle: CSSProperties = {
                  ...(columnProps?.style || {}),
                  ...(rowProps.style || {}),
                  ...(cellProps?.style || {}),
                };
                const mergedCss: CssFromFunction = [
                  ...(meta?.css || []),
                  ...(meta?.cellCss || []),
                  ...(rowProps.css || []),
                  ...(columnProps?.css || []),
                  ...(cellProps?.css || []),
                ];

                return (
                  <Td
                    key={cell.id}
                    style={mergedStyle}
                    css={mergedCss}
                    align={meta?.align || meta?.titleAlign || 'left'}
                    isFirstColumn={cell.column.id === firstColumnId}
                    isLastColumn={cell.column.id === lastColumnId}
                    data-testid={
                      cellProps?.['data-testid'] ??
                      tableV2CellTestId(props['data-testid'], cell.row.index, cell.column.id)
                    }
                    isLastRow={isLastRow}
                    showVerticalBorders={props.showVerticalBorders}
                    {...(meta?.expandOnClick
                      ? {
                          onClick: () => {
                            row.toggleExpanded();
                          },
                          pointerHover: true,
                        }
                      : {})}
                  >
                    {flexRender(columnDef.cell, cell.getContext())}
                  </Td>
                );
              })}
            </Tr>
            {typeof props.ChildComponent !== 'undefined' && row.getIsExpanded() && (
              <ExpandedTr>
                <ExpandedTd>
                  <props.ChildComponent rowValue={row.original} enabled={row.getIsExpanded()} />
                </ExpandedTd>
              </ExpandedTr>
            )}
          </Fragment>
        );
      })}
    </TbodyRoot>
  );
}

// Contains the rows beneath each other
const TbodyRoot = styled.tbody`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

const Tr = styled.tr<{ css?: CssFromFunction }>`
  /* Don't add any styling here, it won't matter because of display: contents */
  display: contents;
`;

const ExpandedTr = styled.tr`
  display: contents;
`;

const ExpandedTd = styled.td`
  width: 100%;
  grid-column-start: 1;
  grid-column-end: -1;
`;

export const Td = styled.td<TableCellCommonStyleProps & { pointerHover?: boolean; isLastRow: boolean }>`
  padding: 0;
  ${props =>
    getTableCellCommonStyle({
      align: props.align,
      isFirstColumn: props.isFirstColumn,
      isLastColumn: props.isLastColumn,
    })};
  ${props => (props.pointerHover === true ? 'cursor: pointer' : '')};
  ${props =>
    props.isLastRow
      ? ''
      : css`
          border-bottom: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
        `}
  ${props =>
    props.showVerticalBorders
      ? css`
          &:not(:last-of-type) {
            border-right: 1px solid ${BouColor.PLATFORM_BORDER_GRAY_LIGHTER};
          }
        `
      : ''}
  word-break: break-word;
  box-sizing: border-box;
  line-height: 1.2;
  /* min-height (not a fixed height) so a cell whose content wraps past one line
  grows the row instead of overflowing into the row below — all cells in a row
  then stretch to the same (grid-auto) track height, keeping borders aligned. */
  min-height: 3.1em;
  ${props => props.css}
`;
